import type { Event } from "@/services/api/events";
import type { PlanType } from "@/models/Plan";

/**
 * JUB-157: a plan is bought for exactly one event. Which event the Pachete
 * page is buying for:
 *  - `event`: one event is meant — the one named by `?event=` (any plan, so
 *    the page shows that event's real plan, JUB-155), or the only event;
 *  - `choose`: several events and none named — the buyer picks one;
 *  - `none`: no events yet — create the event first;
 *  - `unavailable`: `?event=` names an event that is not in the active list
 *    (archived, in the Trash, someone else's or gone). The page looks it up
 *    explicitly; it is never silently replaced by another event (F9).
 * Auto-selection happens only when no `?event=` was given and the account
 * has exactly one active event. There is no "buy without an event" outcome.
 */
export type PurchaseEventResolution =
  | { kind: "event"; uuid: string }
  | { kind: "choose" }
  | { kind: "none" }
  | { kind: "unavailable"; uuid: string };

const PLAN_RANK: Record<PlanType, number> = { free: 0, standard: 1, premium: 2 };

export function eventPlanOf(event: Pick<Event, "plan"> | null | undefined): PlanType {
  const plan = (event?.plan ?? "free").toLowerCase() as PlanType;
  return plan in PLAN_RANK ? plan : "free";
}

export function planRank(plan: PlanType): number {
  return PLAN_RANK[plan] ?? 0;
}

/** An event can still be upgraded (free → standard/premium, standard → premium). */
export function isUpgradable(event: Pick<Event, "plan">): boolean {
  return planRank(eventPlanOf(event)) < PLAN_RANK.premium;
}

export function resolvePurchaseEvent(events: Event[], requestedUuid?: string | null): PurchaseEventResolution {
  if (requestedUuid) {
    return events.some((event) => event.uuid === requestedUuid)
      ? { kind: "event", uuid: requestedUuid }
      : { kind: "unavailable", uuid: requestedUuid };
  }
  if (events.length === 0) {
    return { kind: "none" };
  }
  if (events.length === 1) {
    return { kind: "event", uuid: events[0].uuid };
  }
  return { kind: "choose" };
}
